import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageRouteHeadline } from "@/components/Layout/PageRouteHeadline";
import { NeedHelpButton } from "@/components/need-help-button";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import { presentMutationError, toastError, toastWarning } from "../../../utils/showApiErrorToast";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
} from "../Statistics/components/statisticsEmptyState";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Flex,
} from "@chakra-ui/react";
import { toast } from "sonner";
import { useHistory } from "react-router-dom";
import { deleteReportStrategy, listReportStrategies, updateReportStrategy } from "../../../services/reportsApi";
import type { ReportStrategyResponse } from "../../../types/reports";
import { StrategiesTable, type StrategyListTab } from "./StrategiesTable";
import { CreateStrategyDialog } from "./CreateStrategyDialog";
import { strategiesMainCardClassName } from "./strategiesCardShell";
import { useActiveBrokerAccount } from "../../../hooks/useActiveBrokerAccount";
import { strategyWorkspacePath } from "../Workspace/workspaceRoutes";

export default function Strategies(): JSX.Element {
  const history = useHistory();
  const shadcnInset = useShadcnInsetChrome();
  const { setActions: setInsetHeaderActions } = useShadcnInsetHeaderActions();
  const pageMinHeight = statisticsPageMinHeight(shadcnInset);
  const { activeAccountCode, activeBrokerName } = useActiveBrokerAccount();

  const FlexAny = Flex as any;
  const [strategyListTab, setStrategyListTab] = useState<StrategyListTab>("saved");
  const [query, setQuery] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [strategies, setStrategies] = useState<ReportStrategyResponse[]>([]);
  const [archivingStrategyId, setArchivingStrategyId] = useState<string | null>(null);
  const [bulkActionBusy, setBulkActionBusy] = useState(false);
  const [restoringStrategyId, setRestoringStrategyId] = useState<string | null>(null);
  const [deletingStrategyId, setDeletingStrategyId] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const latestLoadSeq = useRef(0);

  const matchesStrategyQuery = useCallback((strategy: ReportStrategyResponse, q: string) => {
    const n = (strategy.name ?? "").toLowerCase();
    const d = (strategy.description ?? "").toLowerCase();
    return n.includes(q) || d.includes(q) || strategy.id.toLowerCase().includes(q);
  }, []);

  const savedStrategies = useMemo(() => {
    const q = query.trim().toLowerCase();
    return strategies.filter((s) => {
      if (s.is_archived) return false;
      return q ? matchesStrategyQuery(s, q) : true;
    });
  }, [matchesStrategyQuery, query, strategies]);

  const archivedStrategies = useMemo(() => {
    const q = query.trim().toLowerCase();
    return strategies.filter((s) => {
      if (!s.is_archived) return false;
      return q ? matchesStrategyQuery(s, q) : true;
    });
  }, [matchesStrategyQuery, query, strategies]);

  const loadStrategies = useCallback(async () => {
    const seq = ++latestLoadSeq.current;
    setListLoading(true);
    setListError(null);
    try {
      const data = await listReportStrategies({
        include_archived: true,
        limit: 200,
        offset: 0,
        ...(activeAccountCode
          ? {
              broker_account_code: activeAccountCode,
              ...(activeBrokerName ? { broker_name: activeBrokerName } : {}),
            }
          : {}),
      });
      if (seq !== latestLoadSeq.current) return;
      setStrategies(Array.isArray(data?.strategies) ? data.strategies : []);
    } catch (e) {
      if (seq !== latestLoadSeq.current) return;
      setStrategies([]);
      setListError(e instanceof Error ? e.message : "Failed to load strategies.");
    } finally {
      if (seq !== latestLoadSeq.current) return;
      setListLoading(false);
    }
  }, [activeAccountCode, activeBrokerName]);

  useEffect(() => {
    void loadStrategies();
  }, [loadStrategies]);

  const openStrategy = useCallback(
    (strategy: ReportStrategyResponse) => {
      history.push(strategyWorkspacePath(strategy.id));
    },
    [history]
  );

  const handleStrategyCreated = useCallback(
    (created: ReportStrategyResponse) => {
      setStrategyListTab("saved");
      setStrategies((prev) => [created, ...prev.filter((s) => s.id !== created.id)]);
      void loadStrategies();
    },
    [loadStrategies]
  );

  const archiveStrategyById = useCallback(
    async (strategyId: string) => {
      setArchivingStrategyId(strategyId);
      try {
        await updateReportStrategy(strategyId, { is_archived: true });
        toast.success("Strategy archived");
        await loadStrategies();
      } catch (e) {
        presentMutationError(e, "Archive failed");
      } finally {
        setArchivingStrategyId(null);
      }
    },
    [loadStrategies]
  );

  const restoreStrategyById = useCallback(
    async (strategyId: string) => {
      setRestoringStrategyId(strategyId);
      try {
        await updateReportStrategy(strategyId, { is_archived: false });
        toast.success("Strategy restored");
        await loadStrategies();
      } catch (e) {
        presentMutationError(e, "Restore failed");
      } finally {
        setRestoringStrategyId(null);
      }
    },
    [loadStrategies]
  );

  const deleteStrategyById = useCallback(
    async (strategy: ReportStrategyResponse) => {
      const label = strategy.name?.trim() || "this strategy";
      if (
        !window.confirm(
          `Delete "${label}" permanently? All policy versions will be removed. Existing reports will stay but will no longer be linked to this strategy. This cannot be undone.`
        )
      ) {
        return;
      }
      setDeletingStrategyId(strategy.id);
      try {
        await deleteReportStrategy(strategy.id);
        toast.success("Strategy deleted");
        await loadStrategies();
      } catch (e) {
        presentMutationError(e, "Delete failed");
      } finally {
        setDeletingStrategyId(null);
      }
    },
    [loadStrategies]
  );

  const bulkArchiveStrategies = useCallback(
    async (strategyIds: string[]): Promise<string[]> => {
      const uniqueIds = Array.from(new Set(strategyIds));
      if (uniqueIds.length === 0) return [];
      if (!window.confirm(`Archive ${uniqueIds.length} selected strateg${uniqueIds.length === 1 ? "y" : "ies"}?`)) {
        return [];
      }
      setBulkActionBusy(true);
      const archivedIds: string[] = [];
      const failures: string[] = [];
      try {
        for (const id of uniqueIds) {
          try {
            await updateReportStrategy(id, { is_archived: true });
            archivedIds.push(id);
          } catch (e) {
            failures.push(`${id}: ${e instanceof Error ? e.message : "Could not archive strategy."}`);
          }
        }
        if (archivedIds.length > 0) await loadStrategies();
        if (failures.length === 0) {
          toast.success(`${archivedIds.length} strateg${archivedIds.length === 1 ? "y" : "ies"} archived`);
        } else if (archivedIds.length > 0) {
          toastWarning(`Archived ${archivedIds.length} of ${uniqueIds.length}`, {
            description: failures.slice(0, 3).join(" · "),
          });
        } else {
          toastError("Bulk archive failed", { description: failures[0] });
        }
        return archivedIds;
      } finally {
        setBulkActionBusy(false);
      }
    },
    [loadStrategies]
  );

  const bulkRestoreStrategies = useCallback(
    async (strategyIds: string[]): Promise<string[]> => {
      const uniqueIds = Array.from(new Set(strategyIds));
      if (uniqueIds.length === 0) return [];
      if (!window.confirm(`Restore ${uniqueIds.length} selected strateg${uniqueIds.length === 1 ? "y" : "ies"}?`)) {
        return [];
      }
      setBulkActionBusy(true);
      const restoredIds: string[] = [];
      const failures: string[] = [];
      try {
        for (const id of uniqueIds) {
          try {
            await updateReportStrategy(id, { is_archived: false });
            restoredIds.push(id);
          } catch (e) {
            failures.push(`${id}: ${e instanceof Error ? e.message : "Could not restore strategy."}`);
          }
        }
        if (restoredIds.length > 0) await loadStrategies();
        if (failures.length === 0) {
          toast.success(`${restoredIds.length} strateg${restoredIds.length === 1 ? "y" : "ies"} restored`);
        } else if (restoredIds.length > 0) {
          toastWarning(`Restored ${restoredIds.length} of ${uniqueIds.length}`, {
            description: failures.slice(0, 3).join(" · "),
          });
        } else {
          toastError("Bulk restore failed", { description: failures[0] });
        }
        return restoredIds;
      } finally {
        setBulkActionBusy(false);
      }
    },
    [loadStrategies]
  );

  const bulkDeleteStrategies = useCallback(
    async (toDelete: ReportStrategyResponse[]): Promise<string[]> => {
      const unique = Array.from(new Map(toDelete.map((s) => [s.id, s])).values());
      if (unique.length === 0) return [];
      if (
        !window.confirm(
          `Delete ${unique.length} selected strateg${unique.length === 1 ? "y" : "ies"} permanently? This cannot be undone.`
        )
      ) {
        return [];
      }
      setBulkActionBusy(true);
      const deletedIds: string[] = [];
      const failures: string[] = [];
      try {
        for (const strategy of unique) {
          try {
            await deleteReportStrategy(strategy.id);
            deletedIds.push(strategy.id);
          } catch (e) {
            failures.push(`${strategy.name ?? strategy.id}: ${e instanceof Error ? e.message : "Delete failed"}`);
          }
        }
        if (deletedIds.length > 0) await loadStrategies();
        if (failures.length === 0) {
          toast.success(`${deletedIds.length} strateg${deletedIds.length === 1 ? "y" : "ies"} deleted`);
        } else if (deletedIds.length > 0) {
          toastWarning(`Deleted ${deletedIds.length} of ${unique.length}`, {
            description: failures.slice(0, 3).join(" · "),
          });
        } else {
          toastError("Bulk delete failed", { description: failures[0] });
        }
        return deletedIds;
      } finally {
        setBulkActionBusy(false);
      }
    },
    [loadStrategies]
  );

  const strategiesHelpHeaderActions = useMemo(
    () => <NeedHelpButton href="https://help.tesseratrades.com/strategies" />,
    []
  );

  useEffect(() => {
    if (!shadcnInset) {
      setInsetHeaderActions(null);
      return;
    }
    setInsetHeaderActions(strategiesHelpHeaderActions);
    return () => setInsetHeaderActions(null);
  }, [setInsetHeaderActions, shadcnInset, strategiesHelpHeaderActions]);

  return (
    <FlexAny
      data-strategies-page=""
      direction="column"
      pt={shadcnInset ? 0 : { base: "100px", md: "80px", lg: "40px" }}
      pb="24px"
      px={shadcnInset ? 0 : { base: "20px", xl: "40px" }}
      gap="24px"
      w="100%"
      minW={0}
      minH={pageMinHeight}
    >
      {!shadcnInset ? <PageRouteHeadline>Strategies</PageRouteHeadline> : null}

      {listLoading && strategies.length === 0 ? (
        <Flex flex="1" minH={0} w="100%" align="center" justify="center">
          <StatisticsEmptyState
            variant="loading"
            fillParent
            title="Loading strategies"
            description="Please wait while we load your strategies."
          />
        </Flex>
      ) : (
        <div
          data-strategy-main-card=""
          data-strategies-list-card=""
          className={strategiesMainCardClassName}
        >
          {listError ? (
            <Alert status="error" borderRadius="12px">
              <AlertIcon />
              <AlertDescription>{listError}</AlertDescription>
            </Alert>
          ) : null}
          <StrategiesTable
          savedRows={savedStrategies}
          archivedRows={archivedStrategies}
          listTab={strategyListTab}
          onListTabChange={setStrategyListTab}
          onOpenStrategy={openStrategy}
          query={query}
          onQueryChange={setQuery}
          onNewStrategy={() => setCreateDialogOpen(true)}
          onArchiveStrategy={(id) => void archiveStrategyById(id)}
          onRestoreStrategy={(id) => void restoreStrategyById(id)}
          onDeleteStrategy={(s) => void deleteStrategyById(s)}
          onBulkArchive={bulkArchiveStrategies}
          onBulkRestore={bulkRestoreStrategies}
          onBulkDelete={bulkDeleteStrategies}
          bulkActionBusy={bulkActionBusy}
          archivingStrategyId={archivingStrategyId}
          restoringStrategyId={restoringStrategyId}
          deletingStrategyId={deletingStrategyId}
        />
        </div>
      )}

      <CreateStrategyDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={(created) => void handleStrategyCreated(created)}
        brokerAccountCode={activeAccountCode}
        brokerName={activeBrokerName}
      />
    </FlexAny>
  );
}

